import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import WarmupSettings from './WarmupSettings';
import UserProfileTab from './UserProfileTab';
import NutritionistSettingsTab from './NutritionistSettingsTab';
import { CoachProfileSettingsService } from '../services/coachProfileSettingsService';
import type {
  CoachProfileDefaults,
  CoachProfilePolicies,
  CoachProfilePresetDefinition,
  CoachProfilePresetsResponse,
  CoachProfileSettingsData,
} from '../services/types';

// ---------------------------------------------------------------------------
// Bulgarian label maps
// ---------------------------------------------------------------------------

const PRIMARY_GOAL_LABELS: Record<string, string> = {
  fat_loss: 'Сваляне на мазнини',
  muscle_gain: 'Мускулна маса',
  recomposition: 'Рекомпозиция',
  performance: 'Представяне',
  general_fitness: 'Обща кондиция',
};

const PHASE_LABELS: Record<string, string> = {
  cut: 'Дефицит (Cut)',
  bulk: 'Покачване (Bulk)',
  maintenance: 'Поддържане',
  recomp: 'Рекомпозиция',
};

const TRAINING_PRIORITY_LABELS: Record<string, string> = {
  strength: 'Сила',
  hypertrophy: 'Хипертрофия',
  endurance: 'Издръжливост',
  power: 'Мощност',
  mixed: 'Смесен',
};

const EXPERIENCE_LEVEL_LABELS: Record<string, string> = {
  beginner: 'Начинаещ',
  intermediate: 'Средно напреднал',
  advanced: 'Напреднал',
  elite: 'Елит',
};

const RECOVERY_CAPACITY_LABELS: Record<string, string> = {
  low: 'Ниска',
  moderate: 'Умерена',
  high: 'Висока',
};

const CAPABILITY_LABELS: Record<string, string> = {
  training_progression: 'Тренировъчен прогрес',
  nutrition_advice: 'Хранителни съвети',
  cardio_advice: 'Кардио съвети',
  activity_combination_advice: 'Комбиниране на активности',
  recovery_advice: 'Съвети за възстановяване',
  exercise_substitution_advice: 'Смяна на упражнения',
  body_composition_guidance: 'Ръководство за тялото',
};

const POLICY_LABELS: Record<string, string> = {
  allow_calorie_targets: 'Калорийни цели',
  allow_macro_targets: 'Макро цели',
  allow_cardio_frequency_changes: 'Промяна на кардио честотата',
  allow_same_day_cardio_and_lifting: 'Кардио и силова в един ден',
  allow_double_sessions: 'Двойни тренировки',
  allow_aggressive_weight_loss_advice: 'Агресивни съвети за отслабване',
  allow_aggressive_weight_gain_advice: 'Агресивни съвети за покачване',
  allow_exercise_swaps: 'Смяна на упражнения',
  allow_volume_reduction: 'Намаляване на обема',
  allow_deload_suggestions: 'Предложения за делоуд',
};

const ADVICE_SCOPE_LABELS: Record<string, string> = {
  strict: 'Стриктен',
  guided: 'Насочен',
  expanded: 'Разширен',
  coach_mode: 'Треньорски режим',
};

// ---------------------------------------------------------------------------
// Default profile shape when backend returns null (new user)
// ---------------------------------------------------------------------------

const DEFAULT_POLICIES: CoachProfilePolicies = {
  allow_calorie_targets: false,
  allow_macro_targets: false,
  allow_cardio_frequency_changes: false,
  allow_same_day_cardio_and_lifting: false,
  allow_double_sessions: false,
  allow_aggressive_weight_loss_advice: false,
  allow_aggressive_weight_gain_advice: false,
  allow_exercise_swaps: true,
  allow_volume_reduction: true,
  allow_deload_suggestions: true,
};

function makeDefaultDraft(): Omit<CoachProfileSettingsData, 'id' | 'createdAt' | 'updatedAt'> {
  return {
    presetName: null,
    capabilities: ['training_progression'],
    primaryGoal: 'general_fitness',
    secondaryGoal: null,
    phase: 'maintenance',
    trainingPriority: 'mixed',
    experienceLevel: 'intermediate',
    recoveryCapacity: 'moderate',
    policies: { ...DEFAULT_POLICIES },
    adviceScope: 'guided',
    customNotes: null,
  };
}

type Draft = ReturnType<typeof makeDefaultDraft>;

function draftFromProfile(p: CoachProfileSettingsData): Draft {
  return {
    presetName: p.presetName,
    capabilities: [...p.capabilities],
    primaryGoal: p.primaryGoal,
    secondaryGoal: p.secondaryGoal,
    phase: p.phase,
    trainingPriority: p.trainingPriority,
    experienceLevel: p.experienceLevel,
    recoveryCapacity: p.recoveryCapacity,
    policies: { ...p.policies },
    adviceScope: p.adviceScope,
    customNotes: p.customNotes,
  };
}

// ---------------------------------------------------------------------------
// Section heading helper
// ---------------------------------------------------------------------------

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 3, mb: 1.5 }}>
      {children}
    </Typography>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function Settings() {
  const [tab, setTab] = useState(0);
  const [profile, setProfile] = useState<CoachProfileSettingsData | null>(null);
  const [defaults, setDefaults] = useState<CoachProfileDefaults | null>(null);
  const [presets, setPresets] = useState<CoachProfilePresetsResponse | null>(null);
  const [draft, setDraft] = useState<Draft>(makeDefaultDraft());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // -----------------------------------------------------------------------
  // Load data
  // -----------------------------------------------------------------------

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const [profileRes, presetsRes] = await Promise.all([
          CoachProfileSettingsService.getProfile(),
          CoachProfileSettingsService.getPresets(),
        ]);
        if (cancelled) return;
        setDefaults(profileRes.defaults);
        setPresets(presetsRes);
        if (profileRes.profile) {
          setProfile(profileRes.profile);
          setDraft(draftFromProfile(profileRes.profile));
        }
      } catch {
        if (!cancelled) setError('Неуспешно зареждане на профила.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => { cancelled = true; };
  }, []);

  // -----------------------------------------------------------------------
  // Dirty detection
  // -----------------------------------------------------------------------

  const dirty = useMemo(() => {
    const base = profile ? draftFromProfile(profile) : makeDefaultDraft();
    return JSON.stringify(draft) !== JSON.stringify(base);
  }, [draft, profile]);

  // -----------------------------------------------------------------------
  // Handlers
  // -----------------------------------------------------------------------

  const updateDraft = useCallback((patch: Partial<Draft>) => {
    setDraft((prev) => ({ ...prev, ...patch, presetName: null }));
    setSuccess(null);
  }, []);

  const handleCapabilityToggle = useCallback((cap: string) => {
    setDraft((prev) => {
      const next = prev.capabilities.includes(cap)
        ? prev.capabilities.filter((c) => c !== cap)
        : [...prev.capabilities, cap];
      return { ...prev, capabilities: next, presetName: null };
    });
    setSuccess(null);
  }, []);

  const handlePolicyToggle = useCallback((key: keyof CoachProfilePolicies) => {
    setDraft((prev) => ({
      ...prev,
      policies: { ...prev.policies, [key]: !prev.policies[key] },
      presetName: null,
    }));
    setSuccess(null);
  }, []);

  const handleApplyPreset = useCallback(async (presetKey: string) => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      const res = await CoachProfileSettingsService.applyPreset(presetKey);
      setProfile(res.profile);
      setDraft(draftFromProfile(res.profile));
      setSuccess('Пресетът е приложен.');
    } catch {
      setError('Неуспешно прилагане на пресета.');
    } finally {
      setSaving(false);
    }
  }, []);

  const handleSave = useCallback(async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      const res = await CoachProfileSettingsService.updateProfile({
        capabilities: draft.capabilities,
        primaryGoal: draft.primaryGoal,
        secondaryGoal: draft.secondaryGoal,
        phase: draft.phase,
        trainingPriority: draft.trainingPriority,
        experienceLevel: draft.experienceLevel,
        recoveryCapacity: draft.recoveryCapacity,
        policies: draft.policies,
        adviceScope: draft.adviceScope,
        customNotes: draft.customNotes,
      });
      if (res.profile) {
        setProfile(res.profile);
        setDraft(draftFromProfile(res.profile));
      }
      setSuccess('Настройките са запазени.');
    } catch {
      setError('Неуспешно запазване.');
    } finally {
      setSaving(false);
    }
  }, [draft]);

  const handleDiscard = useCallback(() => {
    setDraft(profile ? draftFromProfile(profile) : makeDefaultDraft());
    setSuccess(null);
  }, [profile]);

  // -----------------------------------------------------------------------
  // Render helpers
  // -----------------------------------------------------------------------

  const renderSelect = (
    label: string,
    value: string | null,
    options: string[],
    labels: Record<string, string>,
    onChange: (v: string | null) => void,
    nullable = false,
  ) => (
    <FormControl fullWidth size="small">
      <InputLabel>{label}</InputLabel>
      <Select
        label={label}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
      >
        {nullable && <MenuItem value="">— няма —</MenuItem>}
        {options.map((opt) => (
          <MenuItem key={opt} value={opt}>
            {labels[opt] ?? opt}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  // -----------------------------------------------------------------------
  // Loading / error states
  // -----------------------------------------------------------------------

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !defaults) {
    return <Alert severity="error">{error}</Alert>;
  }

  // -----------------------------------------------------------------------
  // Main render
  // -----------------------------------------------------------------------

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Настройки
      </Typography>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="AI Треньор" />
        <Tab label="Загрявка" />
        <Tab label="Профил" />
        <Tab label="Нутриционист" />
      </Tabs>

      {tab === 1 && <WarmupSettings />}

      {tab === 2 && <UserProfileTab />}

      {tab === 3 && <NutritionistSettingsTab />}

      {tab === 0 && (
        <Box>
          {/* Feedback */}
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

          {/* ---- Presets ---- */}
          <SectionHeading>Пресети</SectionHeading>
          <Grid container spacing={1.5} sx={{ mb: 1 }}>
            {presets && Object.entries(presets).map(([key, preset]: [string, CoachProfilePresetDefinition]) => {
              const isActive = draft.presetName === key;
              return (
                <Grid key={key} size={{ xs: 6, sm: 3 }}>
                  <Card
                    variant="outlined"
                    sx={{
                      height: '100%',
                      borderColor: isActive ? 'primary.main' : 'divider',
                      backgroundColor: isActive ? alpha('#7dd3fc', 0.08) : 'transparent',
                      transition: 'border-color 0.2s, background-color 0.2s',
                    }}
                  >
                    <CardActionArea
                      onClick={() => void handleApplyPreset(key)}
                      disabled={saving}
                      sx={{ height: '100%', p: 1.5 }}
                    >
                      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                        <Typography variant="subtitle2" fontWeight={700}>
                          {preset.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {preset.description}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {/* ---- Goal & Profile ---- */}
          <SectionHeading>Цел и профил</SectionHeading>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              {renderSelect(
                'Основна цел',
                draft.primaryGoal,
                defaults?.primaryGoals ?? [],
                PRIMARY_GOAL_LABELS,
                (v) => updateDraft({ primaryGoal: v ?? 'general_fitness' }),
              )}
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              {renderSelect(
                'Вторична цел',
                draft.secondaryGoal,
                defaults?.primaryGoals ?? [],
                PRIMARY_GOAL_LABELS,
                (v) => updateDraft({ secondaryGoal: v }),
                true,
              )}
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              {renderSelect(
                'Фаза',
                draft.phase,
                defaults?.phases ?? [],
                PHASE_LABELS,
                (v) => updateDraft({ phase: v ?? 'maintenance' }),
              )}
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              {renderSelect(
                'Приоритет на тренировка',
                draft.trainingPriority,
                defaults?.trainingPriorities ?? [],
                TRAINING_PRIORITY_LABELS,
                (v) => updateDraft({ trainingPriority: v ?? 'mixed' }),
              )}
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              {renderSelect(
                'Ниво на опит',
                draft.experienceLevel,
                defaults?.experienceLevels ?? [],
                EXPERIENCE_LEVEL_LABELS,
                (v) => updateDraft({ experienceLevel: v ?? 'intermediate' }),
              )}
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              {renderSelect(
                'Капацитет за възстановяване',
                draft.recoveryCapacity,
                defaults?.recoveryCapacities ?? [],
                RECOVERY_CAPACITY_LABELS,
                (v) => updateDraft({ recoveryCapacity: v ?? 'moderate' }),
              )}
            </Grid>
          </Grid>

          {/* ---- Capabilities ---- */}
          <SectionHeading>Възможности на треньора</SectionHeading>
          <FormGroup>
            <Grid container>
              {(defaults?.capabilities ?? []).map((cap) => (
                <Grid key={cap} size={{ xs: 12, sm: 6 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={draft.capabilities.includes(cap)}
                        onChange={() => handleCapabilityToggle(cap)}
                      />
                    }
                    label={CAPABILITY_LABELS[cap] ?? cap}
                  />
                </Grid>
              ))}
            </Grid>
          </FormGroup>

          {/* ---- Policies ---- */}
          <SectionHeading>Политики</SectionHeading>
          <FormGroup>
            {(Object.keys(draft.policies) as Array<keyof CoachProfilePolicies>).map((key) => (
              <FormControlLabel
                key={key}
                control={
                  <Switch
                    checked={draft.policies[key]}
                    onChange={() => handlePolicyToggle(key)}
                  />
                }
                label={POLICY_LABELS[key] ?? key}
              />
            ))}
          </FormGroup>

          {/* ---- Advice scope ---- */}
          <SectionHeading>Обхват на съветите</SectionHeading>
          <ToggleButtonGroup
            exclusive
            value={draft.adviceScope}
            onChange={(_, v) => { if (v) updateDraft({ adviceScope: v }); }}
            size="small"
            sx={{ flexWrap: 'wrap' }}
          >
            {(defaults?.adviceScopes ?? []).map((scope) => (
              <ToggleButton key={scope} value={scope}>
                {ADVICE_SCOPE_LABELS[scope] ?? scope}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          {/* ---- Custom notes ---- */}
          <SectionHeading>Допълнителни бележки</SectionHeading>
          <TextField
            multiline
            minRows={3}
            maxRows={8}
            fullWidth
            placeholder="Добави бележки за треньора (до 2000 символа)…"
            value={draft.customNotes ?? ''}
            onChange={(e) => updateDraft({ customNotes: e.target.value || null })}
            inputProps={{ maxLength: 2000 }}
          />

          {/* ---- Action bar ---- */}
          <Box sx={{ display: 'flex', gap: 2, mt: 3, justifyContent: 'flex-end' }}>
            {dirty && (
              <Button variant="outlined" onClick={handleDiscard} disabled={saving}>
                Отказ
              </Button>
            )}
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!dirty || saving}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              Запази
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}
